import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Button } from "@mui/material";
import { useForm } from "react-hook-form";
import { Loader as GoogleMapsLoader } from "@googlemaps/js-api-loader";
import { Launch as LaunchIcon } from "@mui/icons-material";

import Input from "components/Input";
import EditMapModal from "components/CoverageAreaMap/components/EditMapModal";

import { useAppDispatch } from "store/hooks";
import {
  handleGetDealers,
  handleUpdateMapRequest,
} from "store/modules/Logistics/thunk.store";

import { drawMap } from "./helpers";

import * as Styled from "./styles";
import { toast } from "utils/toast";

interface CoverageAreaMapProps {
  selectedAreaData?: object;
  coverageAreaId: string;
  dealerId: string;
  currentCoverageArea: any;
}

interface CoverageAreaData {
  mapLink: string;
}

const CoverageAreaMap = ({
  selectedAreaData,
  coverageAreaId,
  currentCoverageArea,
  dealerId,
}: CoverageAreaMapProps) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CoverageAreaData>();

  const dispatch = useAppDispatch();

  const mapDivRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const [isEditMapModalVisible, setIsEditMapModalVisible] = useState(false);
  const [shouldRenderMap, setShouldRenderMap] = useState(false);

  const handleSubmitMapUpdate = async (data: CoverageAreaData) => {
    try {
      await dispatch(
        handleUpdateMapRequest({ ...data, coverageAreaId, dealerId }),
      ).unwrap();

      await dispatch(
        handleGetDealers({
          dealerId,
          coverageAreaId,
        }),
      ).unwrap();
      setIsEditMapModalVisible(false);
    } catch {
      toast("Falha ao atualizar mapa, tente novamente!", {
        type: "error",
      });
    }
  };

  const handleChangeShouldRenderMap = () => {
    setShouldRenderMap((state) => !state);
  };

  const handleChangeEditMapModalVisibility = () => {
    setIsEditMapModalVisible((state) => !state);
  };

  useEffect(() => {
    setShouldRenderMap(false);
  }, [coverageAreaId]);

  useEffect(() => {
    (async () => {
      const gmapsLoader = new GoogleMapsLoader({
        apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY as string,
      });

      await gmapsLoader.load();
      mapRef.current = new google.maps.Map(
        mapDivRef.current as HTMLInputElement,
        {
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControlOptions: { position: 4 },
          zoomControlOptions: { position: 6 },
        },
      );
      drawMap(mapRef.current, selectedAreaData || {}, true);
    })();
  }, [shouldRenderMap, selectedAreaData]);

  return (
    <Styled.Container>
      <EditMapModal
        isOpen={isEditMapModalVisible}
        onClose={handleChangeEditMapModalVisibility}
        onSave={handleSubmitMapUpdate}
      />
      <Typography variant="caption">{t("coverageAreaMap")}</Typography>
      <Styled.MapContainer
        shouldRenderOverlay={!(shouldRenderMap && selectedAreaData)}
      >
        <Styled.MapContent>
          {shouldRenderMap && selectedAreaData ? (
            <Styled.Map ref={mapDivRef} />
          ) : (
            <Styled.MiddleActionsContainer>
              {selectedAreaData ? (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleChangeShouldRenderMap}
                >
                  {t("loadMap").toUpperCase()}
                </Button>
              ) : (
                <Styled.TypeLinkContainer>
                  <Typography display="block" mb={1.5} variant="caption">
                    {t("coverageAreaMap")}
                  </Typography>
                  <Input
                    name="mapLink"
                    error={errors.mapLink}
                    label={t("addCoverageArea")}
                    placeholder="https://www.google.com/maps/d/u/0/edit?mid=312312smnjbnneknNA32sE4&usp=sharing"
                    register={register}
                    fullWidth
                    registerOptions={{
                      required: t("requiredField"),
                      pattern: {
                        value:
                          /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/,
                        message: t("invalidUrl"),
                      },
                    }}
                  />
                </Styled.TypeLinkContainer>
              )}
            </Styled.MiddleActionsContainer>
          )}
        </Styled.MapContent>
      </Styled.MapContainer>

      <Styled.ButtonMap>
        <LaunchIcon fontSize="small" color="inherit" />
        <Styled.Link
          href={currentCoverageArea.map?.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          Link para o mapa: {currentCoverageArea.map?.link}
        </Styled.Link>
      </Styled.ButtonMap>

      <Styled.ActionsContainer>
        <Styled.ActionContainer
          onClick={
            selectedAreaData
              ? handleChangeEditMapModalVisibility
              : handleSubmit(handleSubmitMapUpdate)
          }
          color="secondary"
          variant="text"
          endIcon={selectedAreaData ? <Styled.EditIcon /> : <Styled.SaveIcon />}
        >
          {selectedAreaData ? t("edit") : t("save")}
        </Styled.ActionContainer>
      </Styled.ActionsContainer>
    </Styled.Container>
  );
};

export default CoverageAreaMap;
